export type UserRole = 'Student' | 'Faculty' | 'Admin' | 'Staff';

export interface UserProfile {
  id: string;
  email: string;
  fullName: string;
  role: UserRole;
  departmentId: string | null;
  departmentName: string | null;
  rollNumber: string | null;
  staffRoleName: string | null;
}

export interface LoginResponse {
  token: string;
  expiresAtUtc: string;
  user: UserProfile;
  mustChangePassword: boolean;
  organizationId: string | null;
  organizationName: string | null;
  allowedPages: string[] | null;
}

/** The current user's org + the page keys they may access (used to build the menu/guards). */
export interface PermissionContext {
  organizationId: string | null;
  organizationName: string | null;
  pages: string[];
}

export interface PermissionSet {
  pages: string[];
}

/** The signed-in user's own details for the My Profile card. */
export interface ProfileDetails {
  fullName: string;
  email: string | null;
  role: string;
  regNo: string | null;
  departmentName: string | null;
  programName: string | null;
  batchName: string | null;
  academicYear: string | null;
  institutionName: string | null;
  organizationName: string | null;
  staffRoleName: string | null;
  gender: string | null;
  mobile: string | null;
  fatherName: string | null;
  photoDataUrl: string | null;
  canUploadPhoto: boolean;
}

export interface StaffLogin {
  loginId: string;
  staffId: string;
  staffCode: string;
  name: string;
  institutionName: string;
  loginName: string;
  loginType: string;
  isActive: boolean;
  lastSuccessfulLoginDt: string | null;
  passwordChangeNextLogin: boolean;
}

export interface GenerateLoginsResult {
  created: number;
  alreadyExisted: number;
  totalStaff: number;
}

export type ExamStatus = 'Draft' | 'Published' | 'InProgress' | 'Closed' | 'Archived';
export type AttemptStatus = 'NotStarted' | 'InProgress' | 'Submitted' | 'AutoSubmitted' | 'Disqualified';
export type ReevaluationStatus = 'None' | 'Requested' | 'Completed';
export type QuestionType = 'MultipleChoiceSingle' | 'MultipleChoiceMulti' | 'Descriptive';

export interface RecordInfo {
  createdAt: string;
  createdBy: string | null;
  createdByName: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
  updatedByName: string | null;
  version: number;
}

export interface ExamSummary {
  id: string;
  title: string;
  batchCode: string | null;
  batchName: string | null;
  scheduledStart: string;
  scheduledEnd: string;
  durationMinutes: number;
  status: ExamStatus;
  totalMarks: number;
  questionCount: number;
  attemptStatus: AttemptStatus | null;
  scoreAwarded: number | null;
  evaluationComplete: boolean;
  resultPublishAt: string | null;
  resultsPublished: boolean;
  submissionCount: number;
  batchId: string | null;
  programId: string | null;
  programName: string | null;
  departmentId: string | null;
  departmentName: string | null;
  subjectName: string | null;
  subjectCode: string | null;
}

// ----- Exam schedule (timetable) -----
// An exam period (e.g. "Internal Exam II" for a batch year) with its date range + subject counts.
export interface ExamPeriodDto {
  id: string;
  examName: string;
  batchYearId: string;
  batchYearName: string;
  fromDate: string; // ISO date "YYYY-MM-DD"
  toDate: string;
  subjectCount: number;
  publishedCount: number;
}

export interface ExamPeriodUpsertRequest {
  examName: string;
  batchYearId: string;
  fromDate: string;
  toDate: string;
}

export interface ExamScheduleDto {
  id: string;
  subjectName: string;
  subjectCode: string | null;
  examName: string;
  batchId: string;
  batchCode: string;
  batchName: string;
  programId: string;
  programName: string;
  departmentId: string;
  departmentName: string;
  scheduledStart: string;
  scheduledEnd: string;
  durationMinutes: number;
  invigilator: string | null;
  room: string | null;
  isPublished: boolean;
  batchYearId: string | null;
  batchYearName: string | null;
  examPeriodId: string | null;
}

export interface ExamScheduleUpsertRequest {
  subjectName: string;
  subjectCode: string | null;
  examName: string;
  batchId: string;
  scheduledStart: string;
  scheduledEnd: string;
  durationMinutes: number;
  invigilator: string | null;
  room: string | null;
  examPeriodId: string | null;
}

export interface QuestionOption {
  id: string;
  text: string;
  orderIndex: number;
}

/** A sub-part of a question (e.g. (a)/(b)/(c)), with its own prompt and marks. */
export interface QuestionPart {
  id: string;
  orderIndex: number;
  prompt: string;
  marks: number;
}

export interface Question {
  id: string;
  sectionId: string | null;
  orderIndex: number;
  type: QuestionType;
  prompt: string;
  marks: number;
  allowInk: boolean;
  allowText: boolean;
  options: QuestionOption[];
  choiceGroupId: string | null;
  choiceOrder: number;
  // Sub-parts; empty for a single-prompt question.
  parts: QuestionPart[];
  courseOutcome?: string | null;
  bloomLevels?: number[] | null;
}

export interface SectionInfo {
  id: string;
  title: string;
  instructions: string | null;
  orderIndex: number;
  requiredAnswers: number;
  questionCount: number;
  maxMarks: number;
  questionType: QuestionType;
}

// A Course Outcome defined for an exam (its code + description), shown as the CO legend.
export interface CourseOutcome {
  code: string;
  description: string;
}

export interface ExamPaper {
  examId: string;
  title: string;
  instructions: string | null;
  durationMinutes: number;
  scheduledStart: string;
  scheduledEnd: string;
  totalMarks: number;
  sections: SectionInfo[];
  questions: Question[];
  // Answer-sheet provisioning the student writes on (null = default ruled booklet).
  answerSheets: AnswerSheetConfig | null;
  subjectName: string | null;
  subjectCode: string | null;
  courseOutcomes?: CourseOutcome[] | null;
  // Printed question-paper header context.
  regulation?: string | null;
  organizationName?: string | null;
  departmentName?: string | null;
  organizationLogoDataUrl?: string | null;
}

export interface AuthorOption {
  id: string;
  text: string;
  orderIndex: number;
  isCorrect: boolean;
}

export type RubricCategory = 'General' | 'Text' | 'Diagram' | 'Math' | 'Chemistry' | 'Table';

/** Kinds of content a descriptive answer may contain — the author's hint for routing AI
 *  evaluation (text-only is transcribed; visual/specialist kinds go to a multimodal model). */
export type AnswerContentKind = 'Text' | 'Diagram' | 'Math' | 'Chemistry' | 'Table';

export interface RubricCriterion {
  id: string;
  orderIndex: number;
  title: string;
  description: string | null;
  category: RubricCategory;
  maxPoints: number;
}

export interface Rubric {
  id: string;
  questionId: string;
  description: string | null;
  aiAssistedGrading: boolean;
  totalMaxPoints: number;
  criteria: RubricCriterion[];
}

export interface UpsertRubricCriterionRequest {
  title: string;
  description: string | null;
  category: RubricCategory;
  maxPoints: number;
}

export interface UpsertRubricRequest {
  description: string | null;
  aiAssistedGrading: boolean;
  criteria: UpsertRubricCriterionRequest[];
}

export interface AuthorQuestion {
  id: string;
  sectionId: string | null;
  orderIndex: number;
  type: QuestionType;
  prompt: string;
  marks: number;
  allowInk: boolean;
  allowText: boolean;
  options: AuthorOption[];
  rubric: Rubric | null;
  expectedAnswerContent: AnswerContentKind[];
  choiceGroupId: string | null;
  choiceOrder: number;
  parts: QuestionPart[] | null;
  courseOutcome?: string | null;
  bloomLevels?: number[] | null;
}

export interface AuthorBatch {
  id: string;
  code: string;
  name: string;
  programId: string;
}

export interface AuthorExam {
  id: string;
  title: string;
  instructions: string | null;
  batchId: string | null;
  batchCode: string | null;
  batchName: string | null;
  scheduledStart: string;
  scheduledEnd: string;
  durationMinutes: number;
  status: ExamStatus;
  totalMarks: number;
  sections: SectionInfo[];
  questions: AuthorQuestion[];
  batches: AuthorBatch[] | null;
  answerSheets: AnswerSheetConfig | null;
  subjectName: string | null;
  subjectCode: string | null;
  courseOutcomes?: CourseOutcome[] | null;
  regulation?: string | null;
}

export interface UpdateExamRequest {
  title: string;
  instructions: string | null;
  scheduledStart: string;
  scheduledEnd: string;
  durationMinutes: number;
  batchIds: string[];
  answerSheets?: AnswerSheetConfig | null;
  subjectName?: string | null;
  subjectCode?: string | null;
  courseOutcomes?: CourseOutcome[] | null;
  regulation?: string | null;
}

export interface CreateSectionRequest {
  title: string;
  instructions: string | null;
  requiredAnswers: number;
  questionType: QuestionType;
}

export interface UpdateSectionRequest {
  title: string;
  instructions: string | null;
  requiredAnswers: number;
}

export interface CreateQuestionOptionRequest {
  text: string;
  isCorrect: boolean;
}

/** One sub-part to create/replace (descriptive only). */
export interface CreateQuestionPartInput {
  prompt: string;
  marks: number;
}

export interface CreateQuestionRequest {
  type: QuestionType;
  prompt: string;
  marks: number;
  allowInk: boolean;
  allowText: boolean;
  sectionId: string | null;
  options: CreateQuestionOptionRequest[] | null;
  expectedAnswerContent: AnswerContentKind[] | null;
  parts?: CreateQuestionPartInput[] | null;
  courseOutcome?: string | null;
  bloomLevels?: number[] | null;
}

export interface EitherOrAlternativeRequest {
  prompt: string;
  options: CreateQuestionOptionRequest[] | null;
  expectedAnswerContent: AnswerContentKind[] | null;
  parts?: CreateQuestionPartInput[] | null;
  courseOutcome?: string | null;
  bloomLevels?: number[] | null;
}

export interface CreateEitherOrRequest {
  type: QuestionType;
  marks: number;
  allowInk: boolean;
  allowText: boolean;
  sectionId: string | null;
  altA: EitherOrAlternativeRequest;
  altB: EitherOrAlternativeRequest;
}

export interface UpdateQuestionRequest {
  prompt: string;
  marks: number;
  allowInk: boolean;
  allowText: boolean;
  sectionId: string | null;
  options: CreateQuestionOptionRequest[] | null;
  expectedAnswerContent: AnswerContentKind[] | null;
  // Replaces the question's sub-parts wholesale. Empty list = make it a single question.
  parts?: CreateQuestionPartInput[] | null;
  courseOutcome?: string | null;
  bloomLevels?: number[] | null;
}

export interface CreateExamRequest {
  title: string;
  instructions: string | null;
  batchId?: string | null;
  scheduledStart: string;
  scheduledEnd: string;
  durationMinutes: number;
  organizationId?: string | null;
  // Target batches; empty = visible org-wide.
  batchIds?: string[];
  // Answer-sheet provisioning (ruled/plain main sheet + extra sheets). Null = default booklet.
  answerSheets?: AnswerSheetConfig | null;
  subjectName?: string | null;
  subjectCode?: string | null;
  courseOutcomes?: CourseOutcome[] | null;
  regulation?: string | null;
}

export interface Organization {
  id: string;
  code: string;
  name: string;
  address: string | null;
  city: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  createdAt: string;
  logoDataUrl: string | null;
}

export interface CreateOrganizationRequest {
  code: string;
  name: string;
  address: string | null;
  city: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  logoDataUrl?: string | null;
}

/** Update leaves the code immutable; everything else (incl. logo) may change. */
export interface UpdateOrganizationRequest {
  name: string;
  address: string | null;
  city: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  logoDataUrl?: string | null;
}

export interface Institution {
  id: string;
  code: string;
  name: string;
  organizationId: string;
}

export interface CreateInstitutionRequest {
  organizationId: string;
  code: string;
  name: string;
}

export interface DepartmentDto {
  id: string;
  code: string;
  name: string;
  institutionId: string;
}

export type ProgramType = 'Diploma' | 'MBA' | 'PG' | 'UG' | 'Certification';

export interface ProgramDto {
  id: string;
  code: string;
  name: string;
  departmentId: string;
  type: ProgramType | null;
}

export interface StaffRole {
  id: string;
  code: string;
  name: string;
  organizationId: string;
}

export interface CreateStaffRoleRequest {
  organizationId: string;
  code: string;
  name: string;
}

export interface Student {
  id: string;
  batchId: string;
  batchName: string;
  programId: string;
  programName: string;
  departmentId: string;
  departmentName: string;
  institutionId: string;
  institutionName: string;
  regNo: string;
  firstName: string;
  lastName: string;
  fatherName: string;
  email: string;
  mobile: string;
  gender: string;
  semester?: number | null;
  batchYearCode?: string | null;
}

export interface CreateStudentRequest {
  organizationId: string;
  batchId: string;
  regNo: string;
  firstName: string;
  lastName: string;
  fatherName: string;
  email: string;
  mobile: string;
  gender: string;
  semester?: number | null;
}

export interface StudentLogin {
  loginId: string;
  studentId: string;
  regNo: string;
  name: string;
  batchName: string;
  loginName: string;
  isActive: boolean;
  lastSuccessfulLoginDt: string | null;
  passwordChangeNextLogin: boolean;
}

export interface Staff {
  id: string;
  institutionId: string;
  institutionName: string;
  departmentId: string;
  departmentName: string;
  staffRoleId: string;
  staffRoleName: string;
  firstName: string;
  lastName: string;
  email: string;
  mobile1: string;
  staffCode: string;
  gender: string | null;
}

export interface CreateStaffRequest {
  organizationId: string;
  institutionId: string;
  departmentId: string;
  staffRoleId: string;
  firstName: string;
  lastName: string;
  email: string;
  mobile1: string;
  staffCode: string;
  gender: string | null;
}

export interface BatchDto {
  id: string;
  code: string;
  name: string;
  academicYear: string;
  programId: string;
}

export interface AcademicYear {
  id: string;
  code: string;
  name: string;
  startDate: string; // ISO date "YYYY-MM-DD"
  endDate: string;
}

export interface CreateAcademicYearRequest {
  organizationId: string;
  code: string;
  name: string;
  startDate: string;
  endDate: string;
}

// A batch year (org-level master, e.g. "2024-2028"). Picking it on a batch fills the batch dates.
export interface BatchYear {
  id: string;
  code: string;
  name: string;
  startDate: string; // ISO date "YYYY-MM-DD"
  endDate: string;
}

export interface CreateBatchYearRequest {
  organizationId: string;
  code: string;
  name: string;
  startDate: string;
  endDate: string;
}

// A semester within a batch year (auto-generated in 6-month blocks).
export interface SemesterDto {
  id: string;
  batchYearId: string;
  number: number;
  name: string;
  startDate: string; // ISO date "YYYY-MM-DD"
  endDate: string;
}

export interface UpdateSemesterRequest {
  name: string;
  startDate: string;
  endDate: string;
}

// A batch enriched with its program/department/institution context for the all-batches grid.
export interface BatchGrid {
  id: string;
  code: string;
  name: string;
  academicYear: string;
  programId: string;
  programCode: string;
  programName: string;
  departmentId: string;
  departmentCode: string;
  departmentName: string;
  institutionId: string;
  institutionCode: string;
  institutionName: string;
  startAcademicYearId: string | null;
  startAcademicYearName: string | null;
  endAcademicYearId: string | null;
  endAcademicYearName: string | null;
  effectiveDate: string | null;      // ISO date "YYYY-MM-DD"
  effectiveThruDate: string | null;
  batchYearId: string | null;
  batchYearName: string | null;
}

export interface BatchUpsertRequest {
  code: string;
  name: string;
  startAcademicYearId: string | null;
  endAcademicYearId: string | null;
  effectiveDate: string | null;
  effectiveThruDate: string | null;
  batchYearId: string | null;
}

export interface ClassSectionDto {
  id: string;
  code: string;
  name: string;
  programId: string;
}

// A class section enriched with its program/department/institution context for the grid.
export interface ClassSectionGrid {
  id: string;
  code: string;
  name: string;
  programId: string;
  programCode: string;
  programName: string;
  departmentId: string;
  departmentCode: string;
  departmentName: string;
  institutionId: string;
  institutionCode: string;
  institutionName: string;
}

export interface ClassSectionUpsertRequest {
  code: string;
  name: string;
}

export interface ClassSectionDetail {
  id: string;
  departmentId: string;
  departmentCode: string;
  departmentName: string;
  programId: string;
  programCode: string;
  programName: string;
  academicYearId: string;
  academicYearName: string;
  classSectionId: string;
  classSectionCode: string;
  classSectionName: string;
  institutionId: string;
  effectiveDate: string;       // ISO "YYYY-MM-DD"
  effectiveThruDate: string | null;
}

export interface CreateClassSectionDetailRequest {
  organizationId: string;
  departmentId: string;
  programId: string;
  academicYearId: string;
  classSectionId: string;
  effectiveDate: string;
  effectiveThruDate: string | null;
}

// ----- Exam patterns (per-department question-paper blueprint) -----
export interface ExamPatternSection {
  id: string;
  title: string;
  instructions: string | null;
  orderIndex: number;
  questionType: QuestionType;
  questionCount: number;
  requiredAnswers: number;
  marksPerQuestion: number;
  isEitherOr: boolean;
}

export interface ExamPattern {
  id: string;
  departmentId: string;
  name: string;
  durationMinutes: number;
  totalMarks: number;
  sections: ExamPatternSection[];
}

export interface UpsertExamPatternSectionRequest {
  title: string;
  instructions: string | null;
  questionType: QuestionType;
  questionCount: number;
  requiredAnswers: number;
  marksPerQuestion: number;
  isEitherOr: boolean;
}

export interface UpsertExamPatternRequest {
  departmentId: string;
  name: string;
  durationMinutes: number;
  sections: UpsertExamPatternSectionRequest[];
}

// ----- Syllabi + AI-generated question paper -----
export interface Syllabus {
  id: string;
  departmentId: string;
  title: string;
  courseCode: string;
  semester: number;
  textbook: string | null;
  fileName: string;
  contentType: string;
  createdAt: string;
}

export interface GeneratedOption {
  text: string;
  isCorrect: boolean;
}

export interface GeneratedQuestion {
  prompt: string;
  marks: number;
  options: GeneratedOption[] | null;
  alternativePrompt: string | null;
  alternativeOptions: GeneratedOption[] | null;
}

export interface GeneratedSection {
  title: string;
  instructions: string | null;
  questionType: QuestionType;
  requiredAnswers: number;
  questions: GeneratedQuestion[];
  isEitherOr: boolean;
}

export interface GeneratedPaper {
  title: string;
  instructions: string | null;
  totalMarks: number;
  durationMinutes: number;
  sections: GeneratedSection[];
}

// ----- Bulk import (Excel) -----
export interface ImportRowError {
  row: number;
  message: string;
}

export interface ImportResult {
  created: number;
  failed: number;
  errors: ImportRowError[];
}

export interface Attempt {
  id: string;
  examId: string;
  studentId: string;
  startedAt: string;
  submittedAt: string | null;
  status: AttemptStatus;
  serverNowUtc: string;
  remainingSeconds: number;
}

export interface AnswerRecord {
  id: string;
  questionId: string;
  contentJson: string;
  version: number;
  updatedAt: string;
}

export interface UpsertAnswerResponse {
  answerId: string;
  version: number;
  updatedAt: string;
}

// ----- Polymorphic answer payloads -------------------------------------------------------

/** MCQ payload stored in AnswerRecord.contentJson */
export interface McqAnswer {
  kind: 'mcq';
  selectedOptionIds: string[];
}

export interface TextBlock {
  type: 'text';
  id: string;
  value: string;
}

/** A single captured pointer sample — preserves stylus pressure/tilt when available. */
export interface StrokePoint {
  x: number;
  y: number;
  p: number;          // pressure 0..1
  t: number;          // time delta from start of stroke (ms)
  tx?: number;        // tilt X (-90..90) if available
  ty?: number;        // tilt Y (-90..90) if available
}

/** The kind of pen nib a stroke was drawn with — controls how its width responds to pressure. */
export type PenType = 'ballpoint' | 'gel' | 'fountain' | 'marker';

export interface Stroke {
  id: string;
  color: string;
  width: number;
  pointerType: 'pen' | 'touch' | 'mouse';
  points: StrokePoint[];
  /** Which pen nib drew this stroke (controls rendering). Defaults to gel when absent. */
  pen?: PenType;
}

/** The kind of paper a sheet shows. `ruled`/`plain` are the main answer sheets; the rest are
 *  the extra sheets staff can provision (graph, fine engineering grid, semi-log). */
export type SheetType = 'ruled' | 'plain' | 'graph' | 'engineering' | 'log';

/** The two styles the main answer sheet can take (chosen by staff at exam creation). */
export type MainSheetType = 'ruled' | 'plain';

export interface StrokesBlock {
  type: 'strokes';
  id: string;
  width: number;       // logical page width (A4 portrait coordinate space)
  height: number;      // logical page height, grown up to the A4 cap
  strokes: Stroke[];
  /** The paper this page shows. Falls back to `ruled` (true→ruled, false→plain) for old pages. */
  sheetType?: SheetType;
  /** Legacy ruled flag (pre-sheetType pages). Defaults to ruled when both are absent. */
  ruled?: boolean;
  /** Question number this sheet belongs to (student-entered; for grading attribution). */
  questionNo?: string;
  /** Optional MinIO blob key if the blob has been uploaded for grading */
  blobKey?: string;
}

/** One extra sheet type staff provisions for an exam, with how many each student may use. */
export interface AnswerSheetExtra {
  type: SheetType;
  count: number;
}

/** Answer-sheet provisioning chosen at exam creation: the main sheet style, how many main pages
 *  each student starts with, and the extra sheets they may pull in (graph, engineering, log…). */
export interface AnswerSheetConfig {
  mainSheet: MainSheetType;
  mainPages: number;
  extras: AnswerSheetExtra[];
}

/** Friendly labels for each sheet type (used in toolbars/grids). */
export const SHEET_TYPE_LABELS: Record<SheetType, string> = {
  ruled: 'Ruled',
  plain: 'Plain',
  graph: 'Graph',
  engineering: 'Engineering',
  log: 'Semi-log',
};

export type AnswerBlock = TextBlock;

/** One part's answer inside a multi-part descriptive question. */
export interface DescriptivePartAnswer {
  partId: string;
  pages: StrokesBlock[];
  blocks: TextBlock[];
}

export interface DescriptiveAnswer {
  kind: 'descriptive';
  /** One or more A4-sized ink pages. Always at least one. Used when the question has no parts. */
  pages: StrokesBlock[];
  blocks: TextBlock[];
  /** Present (one entry per question part) when the question is split into sub-parts. */
  parts?: DescriptivePartAnswer[];
}

export type AnswerPayload = McqAnswer | DescriptiveAnswer;

export interface AttemptReviewSummary {
  attemptId: string;
  studentId: string;
  studentName: string;
  rollNumber: string | null;
  startedAt: string;
  submittedAt: string | null;
  status: AttemptStatus;
  scoreAwarded: number | null;
  reevaluationStatus: ReevaluationStatus;
}

export interface ReviewOption {
  id: string;
  text: string;
  orderIndex: number;
  isCorrect: boolean;
}

export interface ReviewQuestionPart {
  id: string;
  orderIndex: number;
  prompt: string;
  marks: number;
  awardedPoints: number | null;
}

export interface ReviewQuestion {
  id: string;
  sectionId: string | null;
  orderIndex: number;
  type: QuestionType;
  prompt: string;
  marks: number;
  allowInk: boolean;
  allowText: boolean;
  rubric: Rubric | null;
  options: ReviewOption[];
  answerJson: string | null;
  expectedAnswerContent: AnswerContentKind[];
  // Per-question marks awarded for this attempt (null = not yet graded). For a multi-part
  // question this is the sum of its parts' awarded marks.
  awardedPoints: number | null;
  choiceGroupId: string | null;
  choiceOrder: number;
  // Sub-parts with their own awarded marks. Null/empty for a single-prompt question.
  parts: ReviewQuestionPart[] | null;
  // Staff red-pen corrections over this answer, as a JSON map { pageId: Stroke[] }. Null if none.
  annotationJson: string | null;
  // The evaluator's per-question remark. Null if none.
  graderRemark: string | null;
}

export interface QuestionScoreInput {
  questionId: string;
  points: number;
}

/** Per-question staff review sent with the grade: red-pen annotation strokes + a remark. */
export interface QuestionReviewInput {
  questionId: string;
  annotationJson: string | null;
  remark: string | null;
}

/** Red-pen annotation strokes for one answer, keyed by the student's ink-page id. */
export type AnnotationMap = Record<string, Stroke[]>;

export interface ReviewBundle {
  attemptId: string;
  examId: string;
  examTitle: string;
  studentName: string;
  rollNumber: string | null;
  status: AttemptStatus;
  startedAt: string;
  submittedAt: string | null;
  scoreAwarded: number | null;
  graderNotes: string | null;
  totalMarks: number;
  sections: SectionInfo[];
  questions: ReviewQuestion[];
  reevaluationStatus: ReevaluationStatus;
  reevaluationReason: string | null;
  reevaluationRequestedAt: string | null;
  subjectName: string | null;
  subjectCode: string | null;
  batchName: string | null;
  resultPublishAt: string | null;
}
